import type { Project, Scenario } from '@finsim/model';
import type { TFunction } from 'i18next';
import type { MonteCarloResult, SimulationResult } from '../../../entities/simulation';
import type { ValueMode } from '../../../entities/view-settings';
import type { Formatters } from '../../../shared/lib/format';
import { hasDebt } from './series';

/** Whether a higher value of a figure is better, worse, or neither (colours the deltas). */
export type Better = 'higher' | 'lower' | 'none';

export interface FigureRow {
  key: string;
  label: string;
  /** The value for a variant, or null when it has none (e.g. P(beats) for the baseline). */
  value: (s: Scenario) => number | null;
  format: (v: number) => string;
  better: Better;
  /** Text instead of a formatted value (e.g. a debt-free date). */
  text?: (s: Scenario) => string;
}

export interface FiguresInput {
  project: Project;
  result: SimulationResult;
  mc: MonteCarloResult | null;
  valueMode: ValueMode;
  dates: readonly Date[];
  fmt: Formatters;
  t: TFunction;
}

/** Text for when a variant is debt-free: a month, "No debt" or "Not within the horizon". */
export function debtFreeText(
  { result, dates, fmt, t }: Pick<FiguresInput, 'result' | 'dates' | 'fmt' | 't'>,
  scenarioId: Scenario['id'],
): string {
  const r = result.scenarios[scenarioId];
  if (!r) return '–';
  if (!hasDebt(r)) return t('kpi.noDebt');
  const month = r.metrics.debtFreeMonth;
  const date = month === null ? undefined : dates[month];
  return date ? fmt.monthLong(date) : t('kpi.notWithinHorizon');
}

/** The key figures compared across variants (Compare view, report summary). */
export function keyFigureRows(input: FiguresInput, baseline: Scenario | undefined): FigureRow[] {
  const { result, mc, valueMode, dates, fmt, t } = input;
  const last = dates.length - 1;
  const year = dates[last] ? fmt.year(dates[last]) : '';
  const det = (s: Scenario) => result.scenarios[s.id];
  const minCash = (s: Scenario): number => {
    const cash = det(s)?.series.cash[valueMode];
    return cash ? Math.min(...cash) : 0;
  };

  const rows: FigureRow[] = [
    {
      key: 'netWorthEnd',
      label: t('compare.rows.netWorthEnd', { year }),
      value: (s) => det(s)?.series.netWorth[valueMode][last] ?? null,
      format: fmt.money,
      better: 'higher',
    },
    {
      key: 'lowestCash',
      label: t('compare.rows.lowestCash'),
      value: minCash,
      format: fmt.money,
      better: 'higher',
    },
    {
      key: 'debtFree',
      label: t('compare.rows.debtFree'),
      value: () => null,
      format: fmt.money,
      better: 'none',
      text: (s) => debtFreeText(input, s.id),
    },
    {
      key: 'interest',
      label: t('compare.rows.interest'),
      value: (s) => det(s)?.metrics.totalInterest ?? null,
      format: fmt.money,
      better: 'lower',
    },
    {
      key: 'income',
      label: t('compare.rows.income'),
      value: (s) => det(s)?.metrics.totalIncome ?? null,
      format: fmt.money,
      better: 'higher',
    },
    {
      key: 'expenses',
      label: t('compare.rows.expenses'),
      value: (s) => det(s)?.metrics.totalExpenses ?? null,
      format: fmt.money,
      better: 'lower',
    },
  ];
  if (mc) {
    const m = (s: Scenario) => mc.scenarios[s.id];
    rows.push(
      {
        key: 'median',
        label: t('compare.rows.medianNetWorth', { year }),
        value: (s) => m(s)?.bands.netWorth[valueMode].p50[last] ?? null,
        format: fmt.money,
        better: 'higher',
      },
      {
        key: 'pCashNegative',
        label: t('compare.rows.pCashNegative'),
        value: (s) => m(s)?.pCashNegativeEver[last] ?? null,
        format: fmt.probability,
        better: 'lower',
      },
    );
    if (baseline) {
      rows.push({
        key: 'pBeats',
        label: t('compare.rows.pBeats'),
        value: (s) => (s.id === baseline.id ? null : (m(s)?.pBeatsBaseline?.[last] ?? null)),
        format: fmt.probability,
        better: 'none',
      });
    }
  }
  return rows;
}

/**
 * A variant's difference from the baseline on a figure, and whether it's good: null when there's
 * no difference to show (the baseline itself, a text figure, no value or no change).
 */
export function figureDelta(
  row: FigureRow,
  scenario: Scenario,
  baseline: Scenario,
): { delta: number; good: boolean | null } | null {
  const v = row.value(scenario);
  const base = row.value(baseline);
  if (v === null || base === null || scenario.id === baseline.id || v === base) return null;
  const delta = v - base;
  return { delta, good: row.better === 'none' ? null : delta > 0 === (row.better === 'higher') };
}

/** "+3 %" / "−€1,200": a delta in the row's own format. */
export function formatDelta(row: FigureRow, delta: number, fmt: Formatters): string {
  return row.format === fmt.probability
    ? `${delta > 0 ? '+' : '−'}${fmt.probability(Math.abs(delta))}`
    : fmt.signed(delta);
}
